import { createContext, useContext, useState, useRef, useCallback, ReactNode, useEffect } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
export type ActiveTab = 'upload' | 'options' | 'aiproof' | 'proofread' | 'plagiarism' | 'citation' | 'reference' | 'readability' | 'templates';

export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
}

export interface PaperOptions {
  paperType: string;
  academicLevel: string;
  citationStyle: string;
  wordCount: string;
  language: string;
  tone: string;
  includeAbstract: boolean;
  includeReferences: boolean;
  includeTableOfContents: boolean;
}

export interface AIProofSettings {
  enabled: boolean;
  intensity: 'light' | 'medium' | 'aggressive';
  targets: { turnitin: boolean; gptzero: boolean; copyleaks: boolean; originality: boolean };
  writingStyle: string;
  techniques: {
    sentenceVariation: boolean;
    vocabularyDiversification: boolean;
    burstinessControl: boolean;
    perplexityBoost: boolean;
    idiomInjection: boolean;
    microToneShifts: boolean;
  };
}

export interface GeneratedPaper {
  title: string;
  abstract: string;
  tableOfContents: string[];
  sections: { heading: string; content: string }[];
  references: string[];
  wordCount: number;
  citationStyle: string;
  paperType: string;
  academicLevel: string;
}

export interface PaperHistoryEntry {
  id: string;
  title: string;
  paperType: string;
  citationStyle: string;
  wordCount: number;
  dateGenerated: string;
  paper: GeneratedPaper;
}

export interface OutlineSection {
  id: string;
  name: string;
  included: boolean;
}

export interface StreamingState {
  isStreaming: boolean;
  currentText: string;
  currentSection: string;
  showCursor: boolean;
}

export interface ComparisonVersion {
  paper: GeneratedPaper;
  settings: {
    paperType: string;
    tone: string;
    citationStyle: string;
    wordCount: string;
  };
}

// ─── Constants ────────────────────────────────────────────────────────────────
export const GENERATE_STEPS = [
  'Analyzing uploaded research files…',
  'Processing topic description…',
  'Structuring paper outline…',
  'Generating introduction…',
  'Writing body sections…',
  'Compiling references…',
  'Applying citation style…',
  'Running AI-proof humanization…',
  'Finalizing and polishing…',
  '✅ Paper generated successfully!',
];

export const PAPER_TYPES = ['Research Paper', 'Essay', 'Thesis', 'Dissertation', 'Literature Review', 'Case Study', 'Report', 'Article'];
export const ACADEMIC_LEVELS = ['High School', 'Undergraduate', 'Graduate', 'PhD', 'Professional'];
export const CITATION_STYLES = ['APA', 'MLA', 'Chicago', 'IEEE', 'Harvard', 'Vancouver'];
export const WORD_COUNTS = ['500–1,000', '1,000–2,500', '2,500–5,000', '5,000–10,000', '10,000+'];
export const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Italian', 'Chinese', 'Japanese'];
export const TONES = ['Academic', 'Formal', 'Analytical', 'Conversational', 'Persuasive', 'Descriptive'];
export const WRITING_STYLES = ['Academic', 'Conversational', 'Formal', 'Analytical', 'Narrative'];
export const ACCEPTED_TYPES = ['PDF', 'DOCX', 'TXT', 'LaTeX', 'MD', 'BIB', 'CSV', 'Images'];
export const QUICK_PROMPTS = [
  'The impact of climate change on global food security',
  'Machine learning applications in healthcare diagnostics',
  "Social media's effect on adolescent mental health",
  'Renewable energy transition in developing economies',
  'Ethical implications of artificial general intelligence',
];

// ─── Paper Builder ────────────────────────────────────────────────────────────
export function buildPaper(topic: string, options: PaperOptions, _aiProof: AIProofSettings, customSections?: OutlineSection[]): GeneratedPaper {
  const topicClean = topic.trim() || 'Artificial Intelligence in Modern Society';
  const titleMap: Record<string, string> = {
    'Research Paper': `An Empirical Investigation into ${topicClean}`,
    'Essay': `${topicClean}: A Critical Analysis`,
    'Thesis': `${topicClean}: Theoretical Frameworks and Empirical Evidence`,
    'Dissertation': `${topicClean}: A Comprehensive Doctoral Study`,
    'Literature Review': `A Systematic Literature Review on ${topicClean}`,
    'Case Study': `${topicClean}: A Detailed Case Study`,
    'Report': `${topicClean}: Findings and Recommendations`,
    'Article': `${topicClean}: Current Perspectives and Future Directions`,
  };
  const title = titleMap[options.paperType] ?? `${topicClean}: An Academic Study`;
  const abstract = `This ${options.paperType.toLowerCase()} presents a comprehensive examination of ${topicClean.toLowerCase()}. Drawing upon a wide range of peer-reviewed sources, empirical data, and theoretical frameworks, the study investigates the multifaceted dimensions of this subject within the context of ${options.academicLevel.toLowerCase()}-level academic discourse. The findings reveal significant patterns and implications that contribute meaningfully to the existing body of knowledge. Employing a ${options.tone.toLowerCase()} analytical approach, this work synthesizes current research to offer nuanced insights and actionable conclusions.`;
  
  // Use custom sections if provided, otherwise use defaults
  let sectionHeadings: string[];
  if (customSections && customSections.length > 0) {
    sectionHeadings = customSections.map(s => s.name);
  } else {
    sectionHeadings = ['Introduction', 'Literature Review', 'Theoretical Framework', 'Methodology', 'Results and Discussion', 'Critical Analysis', 'Implications and Future Directions', 'Conclusion'];
  }

  const toc = sectionHeadings.map((heading, i) => `${i + 1}. ${heading}`);
  if (options.includeReferences) {
    toc.push(`${sectionHeadings.length + 1}. References`);
  }

  // Generate content for each section
  const sections = sectionHeadings.map((heading, i) => {
    let content = '';
    const lowerHeading = heading.toLowerCase();
    
    if (lowerHeading.includes('introduction')) {
      content = `The study of ${topicClean.toLowerCase()} has emerged as one of the most consequential areas of inquiry in contemporary academic discourse. This ${options.paperType.toLowerCase()} seeks to address that need by providing a thorough and systematic examination of the key issues, debates, and developments that define the current landscape.\n\nThe significance of ${topicClean.toLowerCase()} cannot be overstated. From its foundational theoretical underpinnings to its far-reaching practical implications, this subject intersects with a broad array of disciplines, including sociology, economics, technology, and public policy.`;
    } else if (lowerHeading.includes('literature') || lowerHeading.includes('review')) {
      content = `A substantial body of scholarship has accumulated around the topic of ${topicClean.toLowerCase()}, reflecting its growing prominence across multiple fields of study. Seminal works by leading researchers have established the conceptual foundations upon which subsequent investigations have built.\n\nEarly theoretical contributions emphasized the structural dimensions of ${topicClean.toLowerCase()}, arguing that systemic factors play a determinative role in shaping outcomes (Johnson & Smith, 2019). More recent scholarship has turned attention to the intersectional dimensions, examining how factors such as geography, socioeconomic status, and technological access mediate its effects (Williams, 2022; Chen & Park, 2023).`;
    } else if (lowerHeading.includes('theoretical') || lowerHeading.includes('framework')) {
      content = `The theoretical framework underpinning this study draws upon three complementary bodies of theory: systems theory, critical theory, and complexity theory. Together, these frameworks provide a robust conceptual scaffolding for analyzing the multidimensional nature of ${topicClean.toLowerCase()}.`;
    } else if (lowerHeading.includes('method')) {
      content = `This study employs a mixed-methods research design, combining systematic literature review with qualitative thematic analysis and quantitative synthesis of available data. A total of 147 peer-reviewed articles, book chapters, and reports published between 2015 and 2024 were identified as meeting the inclusion criteria and were subjected to full-text analysis.`;
    } else if (lowerHeading.includes('result') || lowerHeading.includes('finding') || lowerHeading.includes('discussion')) {
      content = `The analysis yielded a rich and nuanced picture of ${topicClean.toLowerCase()}, revealing both areas of consensus and significant points of contention within the literature. Six primary themes were identified: definitional diversity, structural determinants, individual agency, technological mediation, equity and access, and governance responses.\n\nA substantial majority of studies (78%) identified structural factors as primary drivers of outcomes related to ${topicClean.toLowerCase()}, with socioeconomic inequality, institutional capacity, and policy environment cited most frequently.`;
    } else if (lowerHeading.includes('analysis') || lowerHeading.includes('critical')) {
      content = `A critical reading of the evidence base reveals several important limitations and blind spots that warrant careful consideration. The literature is heavily skewed toward studies conducted in high-income, English-speaking countries, raising serious questions about the generalizability of findings to other contexts.`;
    } else if (lowerHeading.includes('implication') || lowerHeading.includes('future') || lowerHeading.includes('recommendation')) {
      content = `The findings of this study carry significant implications for theory, research, policy, and practice. At the theoretical level, the analysis supports the value of integrative, multi-level frameworks that can accommodate both structural and agentic dimensions of ${topicClean.toLowerCase()}.\n\nFor policymakers and practitioners, the findings underscore the importance of evidence-based approaches that are sensitive to context and attentive to unintended consequences.`;
    } else if (lowerHeading.includes('conclusion')) {
      content = `This ${options.paperType.toLowerCase()} has provided a comprehensive and critical examination of ${topicClean.toLowerCase()}, drawing upon a wide range of theoretical perspectives and empirical evidence. The central argument advanced is that a nuanced, multi-level, and critically reflexive approach is essential for making sense of the complexities of this subject.\n\nIt is hoped that the insights offered here will stimulate further inquiry, provoke productive debate, and ultimately contribute to more effective and equitable approaches to ${topicClean.toLowerCase()} in theory and in practice.`;
    } else {
      // Generic content for custom sections
      content = `This section examines ${heading.toLowerCase()} in the context of ${topicClean.toLowerCase()}. Through careful analysis of relevant literature and empirical evidence, this discussion illuminates key patterns, relationships, and implications that contribute to our understanding of the subject.\n\nThe evidence suggests that ${heading.toLowerCase()} plays a significant role in shaping outcomes related to ${topicClean.toLowerCase()}. Multiple factors interact in complex ways, requiring careful consideration of both theoretical frameworks and practical applications.`;
    }

    return {
      heading: `${i + 1}. ${heading}`,
      content
    };
  });

  const refStyles: Record<string, string[]> = {
    APA: [
      `Brown, T., Davis, K., & Lee, J. (2020). Structural determinants of ${topicClean.toLowerCase()}: A meta-analysis. *Journal of Social Science Research*, 18(2), 112–134.`,
      `Chen, X., & Park, S. (2023). Intersectional perspectives on ${topicClean.toLowerCase()}. *Global Studies Quarterly*, 7(1), 45–67.`,
      `Johnson, M. A., & Smith, B. C. (2019). Theoretical foundations of ${topicClean.toLowerCase()}. *Annual Review of Social Theory*, 12(4), 201–225.`,
      `Williams, S. (2022). Technology and ${topicClean.toLowerCase()}: Emerging trends. *Technology & Society*, 29(1), 15–38.`,
    ],
    MLA: [
      `Brown, T., et al. "Structural Determinants of ${topicClean}." *Journal of Social Science Research*, vol. 18, no. 2, 2020, pp. 112–134.`,
      `Chen, X., and S. Park. "Intersectional Perspectives on ${topicClean}." *Global Studies Quarterly*, vol. 7, no. 1, 2023, pp. 45–67.`,
      `Williams, S. "Technology and ${topicClean}: Emerging Trends." *Technology & Society*, vol. 29, no. 1, 2022, pp. 15–38.`,
    ],
    Chicago: [
      `Brown, T., K. Davis, and J. Lee. "Structural Determinants of ${topicClean}." *Journal of Social Science Research* 18, no. 2 (2020): 112–134.`,
      `Chen, X., and S. Park. "Intersectional Perspectives on ${topicClean}." *Global Studies Quarterly* 7, no. 1 (2023): 45–67.`,
    ],
    IEEE: [
      `T. Brown, K. Davis, and J. Lee, "Structural determinants of ${topicClean}: A meta-analysis," *J. Social Sci. Res.*, vol. 18, no. 2, pp. 112–134, 2020.`,
      `X. Chen and S. Park, "Intersectional perspectives on ${topicClean}," *Global Stud. Q.*, vol. 7, no. 1, pp. 45–67, 2023.`,
    ],
  };
  const refs = refStyles[options.citationStyle] ?? refStyles['APA'];
  const allText = [abstract, ...sections.map(s => s.content)].join(' ');
  const wordCount = allText.split(/\s+/).filter(Boolean).length;
  return { title, abstract, tableOfContents: toc, sections, references: refs, wordCount, citationStyle: options.citationStyle, paperType: options.paperType, academicLevel: options.academicLevel };
}

// ─── Context ──────────────────────────────────────────────────────────────────
interface PaperWriterContextValue {
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  files: UploadedFile[];
  setFiles: (files: UploadedFile[]) => void;
  handleFiles: (fileList: FileList) => void;
  removeFile: (id: string) => void;
  isDragging: boolean;
  setIsDragging: (v: boolean) => void;
  topicDescription: string;
  setTopicDescription: (v: string) => void;
  paperOptions: PaperOptions;
  setPaperOptions: (opts: PaperOptions) => void;
  aiProof: AIProofSettings;
  setAiProof: (s: AIProofSettings) => void;
  isGenerating: boolean;
  generateStep: number;
  isGenerated: boolean;
  generatedPaper: GeneratedPaper | null;
  canGenerate: boolean;
  handleGenerate: () => void;
  resetPaper: () => void;
  paperHistory: PaperHistoryEntry[];
  loadPaperFromHistory: (id: string) => void;
  deletePaperFromHistory: (id: string) => void;
  clearAllHistory: () => void;
  isHistorySidebarOpen: boolean;
  setIsHistorySidebarOpen: (v: boolean) => void;
  showOutlineBuilder: boolean;
  setShowOutlineBuilder: (v: boolean) => void;
  customOutline: OutlineSection[];
  setCustomOutline: (sections: OutlineSection[]) => void;
  streamingState: StreamingState;
  streamingPaper: GeneratedPaper | null;
  isComparisonMode: boolean;
  setIsComparisonMode: (v: boolean) => void;
  versionA: ComparisonVersion | null;
  versionB: ComparisonVersion | null;
  versionBOptions: PaperOptions;
  setVersionBOptions: (opts: PaperOptions) => void;
  isGeneratingVersionB: boolean;
  generateVersionB: () => void;
  diffHighlightEnabled: boolean;
  setDiffHighlightEnabled: (v: boolean) => void;
  exitComparisonMode: () => void;
}

const PaperWriterContext = createContext<PaperWriterContextValue | null>(null);

const HISTORY_STORAGE_KEY = 'paper-writer-history';
const MAX_HISTORY_ENTRIES = 20;

export function PaperWriterProvider({ children }: { children: ReactNode }) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('upload');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [topicDescription, setTopicDescription] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateStep, setGenerateStep] = useState(0);
  const [isGenerated, setIsGenerated] = useState(false);
  const [generatedPaper, setGeneratedPaper] = useState<GeneratedPaper | null>(null);
  const [paperHistory, setPaperHistory] = useState<PaperHistoryEntry[]>([]);
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
  const [showOutlineBuilder, setShowOutlineBuilder] = useState(false);
  const [customOutline, setCustomOutline] = useState<OutlineSection[]>([]);
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    currentText: '',
    currentSection: '',
    showCursor: true,
  });
  const [streamingPaper, setStreamingPaper] = useState<GeneratedPaper | null>(null);
  
  // Comparison mode state
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [versionA, setVersionA] = useState<ComparisonVersion | null>(null);
  const [versionB, setVersionB] = useState<ComparisonVersion | null>(null);
  const [versionBOptions, setVersionBOptions] = useState<PaperOptions>({
    paperType: 'Research Paper',
    academicLevel: 'Undergraduate',
    citationStyle: 'APA',
    wordCount: '2,500–5,000',
    language: 'English',
    tone: 'Academic',
    includeAbstract: true,
    includeReferences: true,
    includeTableOfContents: false,
  });
  const [isGeneratingVersionB, setIsGeneratingVersionB] = useState(false);
  const [diffHighlightEnabled, setDiffHighlightEnabled] = useState(false);
  
  const generateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPaperHistory(parsed);
      }
    } catch (error) {
      console.error('Failed to load paper history:', error);
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(paperHistory));
    } catch (error) {
      console.error('Failed to save paper history:', error);
    }
  }, [paperHistory]);

  const [paperOptions, setPaperOptions] = useState<PaperOptions>({
    paperType: 'Research Paper',
    academicLevel: 'Undergraduate',
    citationStyle: 'APA',
    wordCount: '2,500–5,000',
    language: 'English',
    tone: 'Academic',
    includeAbstract: true,
    includeReferences: true,
    includeTableOfContents: false,
  });

  const [aiProof, setAiProof] = useState<AIProofSettings>({
    enabled: false,
    intensity: 'medium',
    targets: { turnitin: true, gptzero: true, copyleaks: true, originality: true },
    writingStyle: 'Academic',
    techniques: {
      sentenceVariation: true,
      vocabularyDiversification: true,
      burstinessControl: false,
      perplexityBoost: false,
      idiomInjection: false,
      microToneShifts: false,
    },
  });

  const handleFiles = useCallback((fileList: FileList) => {
    const newFiles: UploadedFile[] = Array.from(fileList).map(f => ({
      id: `${f.name}-${Date.now()}-${Math.random()}`,
      name: f.name,
      size: f.size,
      type: f.type,
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const savePaperToHistory = useCallback((paper: GeneratedPaper) => {
    const entry: PaperHistoryEntry = {
      id: `paper-${Date.now()}-${Math.random()}`,
      title: paper.title,
      paperType: paper.paperType,
      citationStyle: paper.citationStyle,
      wordCount: paper.wordCount,
      dateGenerated: new Date().toISOString(),
      paper,
    };

    setPaperHistory(prev => {
      const updated = [entry, ...prev];
      return updated.slice(0, MAX_HISTORY_ENTRIES);
    });
  }, []);

  const loadPaperFromHistory = useCallback((id: string) => {
    const entry = paperHistory.find(e => e.id === id);
    if (entry) {
      setGeneratedPaper(entry.paper);
      setIsGenerated(true);
      setIsGenerating(false);
    }
  }, [paperHistory]);

  const deletePaperFromHistory = useCallback((id: string) => {
    setPaperHistory(prev => prev.filter(e => e.id !== id));
  }, []);

  const clearAllHistory = useCallback(() => {
    setPaperHistory([]);
  }, []);

  const startStreaming = useCallback((paper: GeneratedPaper) => {
    setStreamingPaper(paper);
    setStreamingState({
      isStreaming: true,
      currentText: '',
      currentSection: 'title',
      showCursor: true,
    });

    // Build the full text to stream
    const streamContent: Array<{ section: string; text: string; delay?: number }> = [
      { section: 'title', text: paper.title.toUpperCase(), delay: 100 },
      { section: 'abstract-heading', text: 'ABSTRACT', delay: 200 },
      { section: 'abstract', text: paper.abstract },
      ...paper.sections.flatMap(s => [
        { section: `section-${s.heading}-heading`, text: s.heading.toUpperCase(), delay: 150 },
        { section: `section-${s.heading}`, text: s.content },
      ]),
      { section: 'references-heading', text: 'REFERENCES', delay: 200 },
      ...paper.references.map((ref, i) => ({
        section: `reference-${i}`,
        text: `${i + 1}. ${ref}`,
        delay: 80,
      })),
    ];

    let contentIndex = 0;
    let wordIndex = 0;
    let currentWords: string[] = [];

    const streamNextWord = () => {
      if (contentIndex >= streamContent.length) {
        // Streaming complete
        setStreamingState({
          isStreaming: false,
          currentText: '',
          currentSection: '',
          showCursor: false,
        });
        setGeneratedPaper(paper);
        setIsGenerated(true);
        setIsGenerating(false);
        savePaperToHistory(paper);
        return;
      }

      const currentContent = streamContent[contentIndex];
      
      if (wordIndex === 0) {
        // Starting a new section
        currentWords = currentContent.text.split(/\s+/);
        setStreamingState(prev => ({
          ...prev,
          currentSection: currentContent.section,
          currentText: '',
        }));

        // Add delay before starting section if specified
        if (currentContent.delay) {
          streamingTimeoutRef.current = setTimeout(() => {
            streamNextWord();
          }, currentContent.delay);
          return;
        }
      }

      if (wordIndex < currentWords.length) {
        // Add next word
        setStreamingState(prev => ({
          ...prev,
          currentText: currentWords.slice(0, wordIndex + 1).join(' '),
        }));
        wordIndex++;

        // Variable delay between words (30-60ms for natural feel)
        const delay = Math.floor(Math.random() * 30) + 30;
        streamingTimeoutRef.current = setTimeout(streamNextWord, delay);
      } else {
        // Move to next section
        contentIndex++;
        wordIndex = 0;
        streamingTimeoutRef.current = setTimeout(streamNextWord, 100);
      }
    };

    // Start streaming
    streamNextWord();

    // Cursor blink animation
    const cursorInterval = setInterval(() => {
      setStreamingState(prev => ({
        ...prev,
        showCursor: !prev.showCursor,
      }));
    }, 500);

    return () => {
      clearInterval(cursorInterval);
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
      }
    };
  }, [savePaperToHistory]);

  const startGeneration = useCallback(() => {
    if (generateIntervalRef.current) clearInterval(generateIntervalRef.current);
    if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
    
    setIsGenerating(true);
    setIsGenerated(false);
    setGeneratedPaper(null);
    setStreamingPaper(null);
    setGenerateStep(0);

    // Build the paper immediately
    const newPaper = buildPaper(topicDescription, paperOptions, aiProof, customOutline);
    
    // Start streaming animation
    startStreaming(newPaper);
    setCustomOutline([]); // Clear custom outline after generation
  }, [topicDescription, paperOptions, aiProof, customOutline, startStreaming]);

  const handleGenerate = useCallback(() => {
    if (!topicDescription.trim() && files.length === 0) return;
    // Show outline builder modal instead of generating immediately
    setShowOutlineBuilder(true);
  }, [topicDescription, files]);

  const resetPaper = useCallback(() => {
    setIsGenerated(false);
    setGeneratedPaper(null);
    setIsComparisonMode(false);
    setVersionA(null);
    setVersionB(null);
    setDiffHighlightEnabled(false);
  }, []);

  const generateVersionB = useCallback(() => {
    if (!topicDescription.trim() && files.length === 0) return;
    
    setIsGeneratingVersionB(true);
    
    // Simulate generation delay
    setTimeout(() => {
      const newPaper = buildPaper(topicDescription, versionBOptions, aiProof, []);
      setVersionB({
        paper: newPaper,
        settings: {
          paperType: versionBOptions.paperType,
          tone: versionBOptions.tone,
          citationStyle: versionBOptions.citationStyle,
          wordCount: versionBOptions.wordCount,
        },
      });
      setIsGeneratingVersionB(false);
    }, 2000);
  }, [topicDescription, versionBOptions, aiProof]);

  const exitComparisonMode = useCallback(() => {
    setIsComparisonMode(false);
    setVersionA(null);
    setVersionB(null);
    setDiffHighlightEnabled(false);
  }, []);

  // When entering comparison mode, save current paper as Version A
  useEffect(() => {
    if (isComparisonMode && generatedPaper && !versionA) {
      setVersionA({
        paper: generatedPaper,
        settings: {
          paperType: paperOptions.paperType,
          tone: paperOptions.tone,
          citationStyle: paperOptions.citationStyle,
          wordCount: paperOptions.wordCount,
        },
      });
      // Initialize Version B options with different defaults
      setVersionBOptions({
        ...paperOptions,
        tone: paperOptions.tone === 'Academic' ? 'Formal' : 'Academic',
        citationStyle: paperOptions.citationStyle === 'APA' ? 'MLA' : 'APA',
      });
    }
  }, [isComparisonMode, generatedPaper, versionA, paperOptions]);

  const canGenerate = topicDescription.trim().length > 0 || files.length > 0;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (generateIntervalRef.current) clearInterval(generateIntervalRef.current);
      if (streamingTimeoutRef.current) clearTimeout(streamingTimeoutRef.current);
    };
  }, []);

  return (
    <PaperWriterContext.Provider value={{
      activeTab, setActiveTab,
      files, setFiles, handleFiles, removeFile,
      isDragging, setIsDragging,
      topicDescription, setTopicDescription,
      paperOptions, setPaperOptions,
      aiProof, setAiProof,
      isGenerating, generateStep, isGenerated, generatedPaper,
      canGenerate, handleGenerate, resetPaper,
      paperHistory, loadPaperFromHistory, deletePaperFromHistory, clearAllHistory,
      isHistorySidebarOpen, setIsHistorySidebarOpen,
      showOutlineBuilder, setShowOutlineBuilder,
      customOutline, setCustomOutline,
      streamingState,
      streamingPaper,
      isComparisonMode, setIsComparisonMode,
      versionA, versionB,
      versionBOptions, setVersionBOptions,
      isGeneratingVersionB, generateVersionB,
      diffHighlightEnabled, setDiffHighlightEnabled,
      exitComparisonMode,
    }}>
      {children}
    </PaperWriterContext.Provider>
  );
}

export function usePaperWriter() {
  const ctx = useContext(PaperWriterContext);
  if (!ctx) throw new Error('usePaperWriter must be used inside PaperWriterProvider');
  return ctx;
}